import AsyncStorage from '@react-native-async-storage/async-storage';
import { log } from '../services/production-logger';


// Storage keys
const USER_AUTH_KEY = '@auth:user';
const USER_PROFILE_KEY = '@auth:profile';

/**
 * Save user authentication data to AsyncStorage
 */
export const saveUserAuth = async (user: any): Promise<void> => {
  if (!user) return;

  // Extract serializable properties from Firebase User object
  const serializableUser = {
    uid: user.uid,
    email: user.email,
    emailVerified: user.emailVerified,
    displayName: user.displayName,
    photoURL: user.photoURL,
    phoneNumber: user.phoneNumber,
    metadata: {
      creationTime: user.metadata?.creationTime,
      lastSignInTime: user.metadata?.lastSignInTime,
    },
    providerData: user.providerData || [],
  };

  try {
    await AsyncStorage.setItem(USER_AUTH_KEY, JSON.stringify(serializableUser));
  } catch (error) {
    log.error('Error saving user auth data:', error);
  }
};

/**
 * Save user profile data to AsyncStorage
 */
export const saveUserProfile = async (profile: any): Promise<void> => {
  if (!profile) return;

  try {
    await AsyncStorage.setItem(USER_PROFILE_KEY, JSON.stringify(profile));
  } catch (error) {
    log.error('Error saving user profile data:', error);
  }
};

/**
 * Get user authentication data from AsyncStorage
 */
export const getUserAuth = async (): Promise<any | null> => {
  try {
    const userJSON = await AsyncStorage.getItem(USER_AUTH_KEY);
    return userJSON ? JSON.parse(userJSON) : null;
  } catch (error) {
    log.error('Error getting user auth data:', error);
    return null;
  }
};

/**
 * Get user profile data from AsyncStorage
 */
export const getUserProfile = async (): Promise<any | null> => {
  try {
    const profileJSON = await AsyncStorage.getItem(USER_PROFILE_KEY);
    return profileJSON ? JSON.parse(profileJSON) : null;
  } catch (error) {
    log.error('Error getting user profile data:', error);
    return null;
  }
};

/**
 * Clear all authentication data from AsyncStorage
 */
export const clearAuthData = async (): Promise<void> => {
  try {
    await AsyncStorage.multiRemove([USER_AUTH_KEY, USER_PROFILE_KEY]);
  } catch (error) {
    log.error('Error clearing auth data:', error);
  }
};

/**
 * Initialize authentication by checking for persistent auth data
 * This can be called during app startup before Firebase initialization
 */
export const initAuth = async (): Promise<{ user: any | null; profile: any | null }> => {
  try {
    const [userAuth, userProfile] = await Promise.all([getUserAuth(), getUserProfile()]);

    return { user: userAuth, profile: userProfile };
  } catch (error) {
    log.error('Error initializing auth:', error);
    return { user: null, profile: null };
  }
};
